import fire, { fuego } from '@store/firebase';

import {
  addHashDocIdList,
  addHashtagAndGetData,
  increaseHashtagCount,
} from '@store/firebase/hashtag/add-hashtag';
import {
  Ihashtag,
  IupdateHashtag,
  IupdateHashtagOrder,
} from '@typings/common/hashtag.interface';
import { deleteHashDocIdList } from './delete-hashtag';

const firestore = fire.firestore();

export async function updateHashtagValue({
  hashtagId,
  collectionPath,
  prevHashtagValue,
  hashtagValue,
}: IupdateHashtag) {
  const hashtagPath = `${collectionPath}Hashtag`;
  const hashtagRef = firestore.collection(hashtagPath);
  const collectionRef = firestore.collection(collectionPath);

  hashtagRef.doc(hashtagId).set({ value: hashtagValue }, { merge: true });
  // 해당 카테고리명을 가진 보드 모두 업데이트
  return collectionRef
    .where('category', '==', prevHashtagValue)
    .get()
    .then((snap) => {
      snap.forEach((doc) => {
        collectionRef.doc(doc.id).update({
          category: hashtagValue,
        });
      });
    });
}

export async function updateHashtagShow({ hashtagId, collectionPath, isShow }) {
  const hashtagPath = `${collectionPath}Hashtag`;
  const hashtagRef = firestore.collection(hashtagPath);

  return hashtagRef.doc(hashtagId).set({ isShow }, { merge: true });
}

/**
 * 태그 문서에 sortType(정렬 방식)을 저장한다.
 * - 태그 설정 패널에서 최신순/오래된 순/지정오더순 탭 변경 시 호출
 * - 하위 호환: 기존 문서에 sortType 필드가 없으면 'latest'로 읽히므로 별도 마이그레이션 불필요
 */
export async function updateHashtagSortType({
  hashtagId,
  collectionPath,
  sortType,
}: {
  hashtagId: string;
  collectionPath: string;
  sortType: 'latest' | 'oldest' | 'order';
}) {
  const hashtagPath = `${collectionPath}Hashtag`;
  return firestore
    .collection(hashtagPath)
    .doc(hashtagId)
    .set({ sortType }, { merge: true });
}

/**
 * 해시태그(또는 게시글) 컬렉션의 order 필드를 배열 인덱스 기준으로 일괄 갱신한다.
 *
 * - newOrderList[0] → order: 0, newOrderList[1] → order: 1, ...
 * - Firestore batch 최대 500건 제한을 고려해 청크 단위로 커밋한다.
 * - order desc 방향 컬렉션을 대상으로 할 때는 호출 전 newOrderList를 역순으로
 *   전달해야 "index 0 = 최고 order 값"이 올바르게 유지된다.
 */
export async function updateHashtagOrder({
  HashtagColPath,
  newOrderList,
}: IupdateHashtagOrder) {
  const hashtagRef = firestore.collection(HashtagColPath);
  const CHUNK = 500;

  for (let i = 0; i < newOrderList.length; i += CHUNK) {
    const batch = firestore.batch();
    newOrderList.slice(i, i + CHUNK).forEach((item, idx) => {
      batch.update(hashtagRef.doc(item.id), { order: i + idx });
    });
    await batch.commit();
  }
}

/**
 * 컬렉션 전체의 order 필드를 createdAt 기준 최신순으로 재정렬한다.
 *
 * - docOrderDir === 'desc' (일반 게시판): 최신 문서가 가장 높은 order → board desc 정렬과 일치
 * - docOrderDir === 'asc'  (스토어 등)  : 최신 문서가 가장 낮은 order → board asc  정렬과 일치
 */
export async function resetOrderByCreatedAt({
  collectionPath,
  docOrderDir = 'desc',
}: {
  collectionPath: string;
  docOrderDir?: 'asc' | 'desc';
}) {
  const snapshot = await firestore
    .collection(collectionPath)
    .orderBy('createdAt', 'desc')
    .get();

  // desc 보드: 오래된 것(뒤)이 order=0, 최신(앞)이 order=N-1 → desc 정렬 시 최신 먼저
  // asc  보드: 최신(앞)이 order=0 → asc 정렬 시 최신 먼저
  const ordered =
    docOrderDir === 'desc' ? [...snapshot.docs].reverse() : snapshot.docs;

  const CHUNK = 500;
  for (let i = 0; i < ordered.length; i += CHUNK) {
    const batch = firestore.batch();
    ordered.slice(i, i + CHUNK).forEach((doc, idx) => {
      batch.update(doc.ref, { order: i + idx });
    });
    await batch.commit();
  }
}

/**
 * 드래그 재정렬 후 게시글들의 `order` 필드를 batch 업데이트한다.
 *
 * orderList에 담긴 현재 order 값들을 추출해 새 위치에 재배분한다.
 * 같은 order 값 집합을 재사용하므로, 다른 페이지 항목과 충돌하지 않는다.
 *
 * - docOrderDir === 'desc': 목록 앞 항목에 더 높은 order 값 배분
 * - docOrderDir === 'asc' : 목록 앞 항목에 더 낮은 order 값 배분
 */
export async function batchUpdateDocOrders({
  collectionPath,
  orderList,
  docOrderDir = 'desc',
}: {
  collectionPath: string;
  orderList: { id: string; order?: number }[];
  docOrderDir?: 'asc' | 'desc';
}) {
  if (!orderList.length) return;

  const existingOrders = orderList
    .map((item) => (typeof item.order === 'number' ? item.order : 0))
    .sort((a, b) => (docOrderDir === 'desc' ? b - a : a - b));

  const CHUNK = 500;
  for (let i = 0; i < orderList.length; i += CHUNK) {
    const batch = firestore.batch();
    orderList.slice(i, i + CHUNK).forEach((item, idx) => {
      batch.update(firestore.collection(collectionPath).doc(item.id), {
        order: existingOrders[i + idx],
      });
    });
    await batch.commit();
  }
}

async function extractNewHashList(
  hashList: Ihashtag[],
  collectionPath: string,
) {
  const hashtagPath = `${collectionPath}Hashtag`;
  const hashtagRef = firestore.collection(hashtagPath);

  // id: 'new'인 태그가 있을 때만 기존 해시태그 목록을 한 번에 조회 (중복 생성 방지)
  const hasNewTags = hashList.some((h) => h.id === 'new');
  let existingHashtags: Ihashtag[] = [];
  if (hasNewTags) {
    const snapshot = await hashtagRef.get();
    existingHashtags = snapshot.docs.map((doc) => ({
      id: doc.id,
      value: doc.data().value,
    }));
  }

  const newHashList = await Promise.all(
    hashList.map(async (hash: Ihashtag, index) => {
      if (hash.id === 'new') {
        // 동일한 value의 해시태그가 이미 존재하면 기존 것을 재사용
        const existing = existingHashtags.find(
          (h) => h.value === hash.value,
        );
        if (existing) {
          return existing;
        }
        const newData = await addHashtagAndGetData({
          collectionPath,
          hashtagValue: hash.value,
          index,
        });
        return newData;
      } else {
        return hash;
      }
    }),
  );

  return newHashList;
}

interface IupdateHashtags {
  hashtagList: Ihashtag[] | undefined | null;
  collectionPath: string;
  docId?: string;
  prevHashIdList: string[] | undefined;
}

export async function updateHashtagsAndGetIdList({
  hashtagList,
  collectionPath,
  docId,
  prevHashIdList,
}: IupdateHashtags) {
  if (!hashtagList) return [];

  // 완전 기존의 태그 id list
  const initialHashIdList = hashtagList.map((hash: Ihashtag) => hash.id);

  // 'new' id 의 태그를 담음
  // -  완전히 새로 추가되는 태그가 있는지 여부
  const isNewList = hashtagList.filter((item) => item.id === 'new');
  const extractHashList: Ihashtag[] = await extractNewHashList(
    hashtagList,
    collectionPath,
  );

  // 'new'id를 가진 친구들을 hashtag에 추가하고, 해당친구들을 포함한 id리스트로 만듬
  let hashIdList: string[] = extractHashList.map((hash: Ihashtag) => hash.id);

  // 기존값을 그대로 유지하는 경우
  if (JSON.stringify(prevHashIdList) === JSON.stringify(initialHashIdList)) {
    return initialHashIdList;
  }

  // 기존값은 없는데 새 태그들이 추가된 경우
  if (!prevHashIdList && hashtagList.length > 0) {
    // hashtag docIdList에 해당 docId 추가
    await Promise.all(
      hashIdList.map((id: string) => {
        addHashDocIdList({
          hashtagId: id,
          collectionPath,
          docId,
        });
      }),
    );
  }

  // 기존값도 있고, 신규값도 있는 경우
  if (prevHashIdList) {
    // 1. 비교해서 새로 생긴 태그 (신규값이 없어도 안전)
    const compareNewHashList: Ihashtag[] = extractHashList.filter(
      (hash: Ihashtag) => !prevHashIdList.includes(hash.id),
    );

    await Promise.all(
      compareNewHashList.map((hash: Ihashtag) => {
        addHashDocIdList({
          hashtagId: hash.id,
          collectionPath,
          docId,
        });
      }),
    );

    // 2. 비교해서 없어진 태그 (신규값이 빈 배열이면 prev 전체가 제거 대상)
    const compareRemovedHashIdList: string[] = prevHashIdList.filter(
      (id: string) => !hashIdList.includes(id),
    );

    await Promise.all(
      compareRemovedHashIdList.map((id) => {
        deleteHashDocIdList({
          hashtagId: id,
          collectionPath,
          docId,
        });
      }),
    );
  }

  // 최종 id 리스트 반환
  return hashIdList;
}
